import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Seo from './Seo'
import ChampionIconWithFallback from '../shared/ChampionIcon'
import { assetUrl, axiosForAPI } from '../helper_functions/utilities'

function ChampionsIndex() {
    const [champions, setChampions] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        const controller = new AbortController()

        async function loadChampions() {
            try {
                const response = await axiosForAPI.get('/champions/', { signal: controller.signal })
                setChampions(response.data || [])
            } catch (requestError) {
                if (requestError?.code === 'ERR_CANCELED') return
                setError('Unable to load champions right now.')
            } finally {
                setLoading(false)
            }
        }

        loadChampions()
        return () => controller.abort()
    }, [])

    return (
        <div className="mx-auto w-full max-w-screen-lg text-zinc-200">
            <div className="mx-4 pt-6 pb-8 custom-lg:mx-0 sm:pt-8 sm:pb-10">
                <Seo
                    title="League of Legends Champion Analytics | WINRATES.GG™"
                    description="Browse champion analytics, builds, runes, matchups, and win rates on WINRATES.GG™."
                    canonical="/analytics/champions"
                />
                <header className="mb-6">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-400">Champion analytics</div>
                    <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-100 sm:text-3xl">Champions</h1>
                    <p className="mt-2 max-w-2xl text-sm text-zinc-400">Select a champion to explore builds, runes, matchups, and analytics.</p>
                </header>

                {loading ? <div className="text-sm text-zinc-500">Loading champions...</div> : null}
                {error ? <div className="rounded-lg border border-rose-900/60 bg-rose-950/20 px-4 py-3 text-sm text-rose-200">{error}</div> : null}
                {!loading && !error ? (
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9">
                        {champions.map((champion) => (
                            <Link
                                key={champion.champion_id}
                                className="group flex flex-col items-center gap-2 rounded-lg border border-zinc-800/80 bg-zinc-950 px-2 py-3 transition hover:border-cyan-800/70 hover:bg-cyan-950/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/70"
                                to={`/analytics/champions/${champion.name_id}`}
                            >
                                <span className="h-14 w-14 overflow-hidden rounded-full border border-zinc-700 bg-zinc-900 sm:h-16 sm:w-16">
                                    <ChampionIconWithFallback
                                        alt={`${champion.name} icon`}
                                        className="h-full w-full scale-[1.18] object-cover transition-transform duration-200 group-hover:scale-[1.24]"
                                        src={assetUrl(`/champion_icons/${champion.name_id}.png`)}
                                    />
                                </span>
                                <span className="w-full truncate text-center text-xs font-medium text-zinc-300 group-hover:text-zinc-100">{champion.name}</span>
                            </Link>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export default ChampionsIndex
